import { useEffect, useState } from "react";
import {
  PencilIcon,
  TrashIcon,
  PlusIcon,
  MenuIcon,
  ChevronDownIcon,
} from "@heroicons/react/outline";
import PageLoader from "../../../components/pageLoader";
import {
  createTicketTypes,
  deleteTicketType,
  getAllTicketTypesByEvent,
  getTicketTypesByEvent,
  updateTicketType,
} from "../../../api/ticketTypesApi";
import ToastContainer from "../../../components/toastContainer";
import Toast from "../../../components/toast";
import TicketFormModal from "./TicketsFormModal";
import DeleteConfirmationModal from "../../../components/deleteModal";
import { CURRENCY_MAP } from "../../../constants";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const TicketTypesTab = ({ eventId }) => {
  const [showAddModal, setShowAddModal] = useState(false);

  const [limit, setLimit] = useState(10);

  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);

  const [allTickets, setAllTickets] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [deleteTicketId, setDeleteTicketId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const totalPages = Math.ceil(total / limit);

  const [editingTicket, setEditingTicket] = useState(null);
  const [saving, setSaving] = useState(false);

  const [toast, setToast] = useState(null);

  const [statusTicket, setStatusTicket] = useState(null); // ticket being edited
  const [newStatus, setNewStatus] = useState("");
  const [statusSaving, setStatusSaving] = useState(false);

  // ── DND ──
  const sensors = useSensors(useSensor(PointerSensor));

  const getPageNumbers = () => {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  };

  const formatCurrency = (amount, currency) => {
    const symbol = CURRENCY_MAP[currency] || currency;
    return `${symbol} ${amount}`;
  };

  const handleTicketSubmit = async (payload) => {
    try {
      setSaving(true);

      if (editingTicket) {
        await updateTicketType(editingTicket.id, payload);
      } else {
        await createTicketTypes(payload);
      }

      setEditingTicket(null);
      setShowAddModal(false);

      fetchTickets();
    } catch (err) {
      setToast({
        type: "error",
        message:
          err?.response?.data?.detail || err?.detail || "Something went wrong",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTicket = async () => {
    try {
      setDeleting(true);
      await deleteTicketType(deleteTicketId);

      setDeleteTicketId(null);

      // Refresh users list
      fetchTickets();
    } catch (err) {
      setToast({
        type: "error",
        message:
          err?.response?.data?.detail || err?.detail || "Something went wrong",
      });
    } finally {
      setDeleting(false);
    }
  };

  // ── DRAG END ──
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = tickets.findIndex((t) => t.id === active.id);
    const newIndex = tickets.findIndex((t) => t.id === over.id);
    const reordered = arrayMove(tickets, oldIndex, newIndex);

    // Optimistically update UI
    setTickets(reordered);

    // Patch only tickets whose sort_order changed
    try {
      await Promise.all(
        reordered.map((ticket, index) => {
          const newSortOrder = index + 1;
          if (ticket.sort_order !== newSortOrder) {
            return updateTicketType(ticket.id, {
              sort_order: newSortOrder,
              event: eventId,
            });
          }
          return Promise.resolve();
        }),
      );
    } catch (err) {
      setToast({
        type: "error",
        message:
          err?.response?.data?.detail || err?.detail || "Something went wrong",
      });
      // Revert to server state on error
      fetchTickets();
    }
  };

  const handleStatusChange = async () => {
    try {
      setStatusSaving(true);
      await updateTicketType(statusTicket.id, {
        admin_status: newStatus,
        event: eventId,
      });
      setStatusTicket(null);
      fetchTickets();
    } catch (err) {
      setToast({
        type: "error",
        message:
          err?.response?.data?.detail || err?.detail || "Something went wrong",
      });
    } finally {
      setStatusSaving(false);
    }
  };

  const fetchTickets = async () => {
    try {
      setLoading(true);
      setError("");

      const allData = await getAllTicketTypesByEvent(999, 0, eventId);
      setAllTickets(allData.results || []);

      const offset = (page - 1) * limit;
      const data = await getTicketTypesByEvent(limit, offset, eventId);
      // Sort by sort_order after fetching
      setTickets(
        (data.results || []).sort((a, b) => a.sort_order - b.sort_order),
      );
      setTotal(data.count || 0);
    } catch (err) {
      setError("Failed to load ticket types.");
    } finally {
      setLoading(false);
    }
  };

  const handleLimitChange = (e) => {
    setPage(1);
    setLimit(Number(e.target.value));
  };

  const statusStyles = {
    live: "bg-emerald-500/10 text-emerald-400",
    on_sale: "bg-emerald-500/10 text-emerald-400",
    draft: "bg-amber-500/10 text-amber-400",
    coming_soon: "bg-amber-500/10 text-amber-400",
    sold_out: "bg-red-500/10 text-red-400",
    refunded: "bg-slate-500/10 text-slate-400",
  };

  const statusClass = (status) =>
    statusStyles[(status || "").toLowerCase()] ||
    "bg-slate-500/10 text-slate-400";

  useEffect(() => {
    fetchTickets();
  }, [page, limit]);

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-m font-semibold text-white">Ticket Types</h3>
        </div>

        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 bg-pink-600 hover:bg-pink-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition"
        >
          <PlusIcon className="w-4 h-4" />
          Add Ticket Category
        </button>
      </div>

      {/* Table */}
      <div className="bg-slate-900/60 border border-white/10 rounded-xl">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <div className="overflow-x-auto">
            {/* DndContext lives OUTSIDE the table to avoid invalid HTML nesting */}

            <table
              className="w-full border-collapse"
              style={{ minWidth: "1000px" }}
            >
              <thead>
                <tr className="border-b border-white/10 text-center text-xs uppercase tracking-wider text-slate-400">
                  <th className="px-2 py-4 w-6"></th>
                  <th className="px-3 py-4">Category Name</th>
                  <th className="px-3 py-4">Type</th>
                  <th className="px-3 py-4">Series</th>
                  <th className="px-3 py-4">Reserved</th>
                  <th className="px-3 py-4">Sold</th>
                  <th className="px-3 py-4">Available</th>
                  <th className="px-3 py-4">Store Status</th>
                  <th className="px-3 py-4">Price (Comm.)</th>
                  <th className="px-3 py-4">Income</th>
                  <th className="px-3 py-4">Commission</th>
                  <th className="px-3 py-4">Status</th>
                  <th className="px-3 py-4 text-right">Actions</th>
                </tr>
              </thead>

              <SortableContext
                items={tickets.map((t) => t.id)}
                strategy={verticalListSortingStrategy}
              >
                <tbody>
                  {tickets.map((tk) => (
                    <SortableRow
                      key={tk.id}
                      tk={tk}
                      formatCurrency={formatCurrency}
                      onEdit={() => setEditingTicket(tk)}
                      onDelete={() => setDeleteTicketId(tk.id)}
                      onStatusClick={(tk) => {
                        setStatusTicket(tk);
                        setNewStatus(tk.admin_status);
                      }}
                    />
                  ))}
                </tbody>
              </SortableContext>
            </table>
          </div>
        </DndContext>
        {/* Empty state placeholder (future) */}
        {tickets.length === 0 && (
          <div className="p-8 text-center text-slate-400">
            No tickets found.
          </div>
        )}
      </div>

      <div className="flex justify-end">
        <div className="flex items-center justify-between px-6 py-4 text-sm text-slate-400">
          <div className="flex items-center justify-center gap-3 px-6 py-4 text-sm text-slate-400">
            {/* ── Rows per page dropdown ── */}
            <div className="flex items-center gap-2">
              <span className="text-slate-400 text-sm">Rows per page:</span>
              <select
                value={limit}
                onChange={handleLimitChange}
                className="bg-slate-800 text-slate-300 border border-white/10 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-pink-500 cursor-pointer"
              >
                {[5, 10, 25, 50].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>

            {totalPages > 1 && (
              <>
                {/* Previous */}
                <button
                  disabled={page === 1}
                  onClick={() => setPage((p) => p - 1)}
                  className={`px-3 py-1 rounded-md border border-white/10 transition
        ${
          page === 1
            ? "opacity-50 cursor-not-allowed"
            : "hover:bg-slate-800 text-white"
        }`}
                >
                  Previous
                </button>

                {/* Page Numbers */}
                <div className="flex gap-1">
                  {getPageNumbers().map((pageNumber) => (
                    <button
                      key={pageNumber}
                      onClick={() => setPage(pageNumber)}
                      className={`px-3 py-1 rounded-md border border-white/10 transition
            ${
              page === pageNumber
                ? "bg-pink-600 text-white"
                : "text-slate-300 hover:bg-slate-800 hover:text-white"
            }`}
                    >
                      {pageNumber}
                    </button>
                  ))}
                </div>

                {/* Next */}
                <button
                  disabled={page === totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className={`px-3 py-1 rounded-md border border-white/10 transition
        ${
          page === totalPages
            ? "opacity-50 cursor-not-allowed"
            : "hover:bg-slate-800 text-white"
        }`}
                >
                  Next
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Status Change Modal ── */}
      {statusTicket && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="w-full max-w-sm bg-slate-900 border border-white/10 rounded-xl shadow-xl">
            {/* Header */}
            <div className="px-6 py-4 border-b border-white/10">
              <h2 className="text-white font-semibold text-sm">
                Change Status
              </h2>
              <p className="text-xs text-slate-400 mt-0.5">
                {statusTicket.name}
              </p>
            </div>

            {/* Options */}
            <div className="px-6 py-4 space-y-2">
              {["live", "draft", "sold_out"].map((s) => (
                <label
                  key={s}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg border cursor-pointer transition ${
                    newStatus === s
                      ? "border-pink-500/50 bg-pink-500/10"
                      : "border-white/10 hover:bg-slate-800"
                  }`}
                >
                  <input
                    type="radio"
                    name="admin_status"
                    value={s}
                    checked={newStatus === s}
                    onChange={() => setNewStatus(s)}
                    className="accent-pink-500"
                  />
                  <span
                    className={`text-xs font-medium capitalize px-2.5 py-0.5 rounded-md ${statusClass(s)}`}
                  >
                    {s.replace("_", " ")}
                  </span>
                </label>
              ))}
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-white/10">
              <button
                onClick={() => setStatusTicket(null)}
                className="px-4 py-2 text-sm rounded-lg text-slate-300 hover:bg-slate-800 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleStatusChange}
                disabled={
                  statusSaving || newStatus === statusTicket.admin_status
                }
                className="px-4 py-2 text-sm rounded-lg text-white bg-pink-600 hover:bg-pink-700 disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                {statusSaving ? "Saving..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}

      <TicketFormModal
        isOpen={showAddModal || !!editingTicket}
        onClose={() => {
          setShowAddModal(false);
          setEditingTicket(null);
        }}
        onSubmit={handleTicketSubmit}
        loading={saving}
        mode={editingTicket ? "edit" : "create"}
        initialData={editingTicket}
        ticketTypes={allTickets}
        eventId={eventId}
      />

      <DeleteConfirmationModal
        isOpen={!!deleteTicketId}
        description="Are you sure you want to delete this ticket type?  This action cannot be undone."
        onClose={() => setDeleteTicketId(null)}
        onConfirm={handleDeleteTicket}
        loading={deleting}
      />

      <ToastContainer>
        {toast && (
          <div className="toast-enter">
            <Toast
              type={toast.type}
              message={toast.message}
              onClose={() => setToast(null)}
            />
          </div>
        )}
      </ToastContainer>
    </div>
  );
};

export default TicketTypesTab;

/* ── SORTABLE ROW ── */

function SortableRow({ tk, formatCurrency, onEdit, onDelete, onStatusClick }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: tk.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    backgroundColor: isDragging ? "rgba(30,41,59,0.8)" : undefined,
    zIndex: isDragging ? 50 : undefined,
    position: isDragging ? "relative" : undefined,
  };

  const statusStyles = {
    live: "bg-emerald-500/10 text-emerald-400",
    on_sale: "bg-emerald-500/10 text-emerald-400",
    draft: "bg-amber-500/10 text-amber-400",
    coming_soon: "bg-amber-500/10 text-amber-400",
    sold_out: "bg-red-500/10 text-red-400",
    refunded: "bg-slate-500/10 text-slate-400",
  };

  const statusClass = (status) =>
    statusStyles[(status || "").toLowerCase()] ||
    "bg-slate-500/10 text-slate-400";

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className="border-b text-center border-white/5 last:border-none hover:bg-slate-800/40 transition"
    >
      {/* DRAG HANDLE */}
      <td className="px-3 py-4 w-8">
        <button
          {...attributes}
          {...listeners}
          className="text-slate-500 hover:text-slate-300 cursor-grab active:cursor-grabbing transition"
          title="Drag to reorder"
        >
          <MenuIcon className="w-4 h-4" />
        </button>
      </td>

      <td className="px-3 py-4 text-sm text-left text-slate-300">{tk.name} {tk.subtitle !== '' ?  `-${tk.subtitle}` : ''}</td>

      <td className="px-3 py-4">
        <div className="text-sm font-medium text-white">{tk.type}</div>
      </td>

      <td className="px-3 py-4 text-sm text-slate-300">{tk.series}</td>

      <td className="px-3 py-4 text-sm text-slate-300">
        {tk.quantity_reserved || "-"}
      </td>

      <td className="px-3 py-4 text-sm text-slate-300">
        {tk.quantity_paid || "-"}
      </td>

      <td className="px-3 py-4 text-sm text-slate-300">
        {tk.quantity_available || "-"}
      </td>

      <td className="px-3 py-4 text-sm text-slate-300">
        <div className="relative group inline-block">
          <span
            className={`px-2.5 py-0.5 rounded-md text-xs font-medium capitalize ${statusClass(tk.display_status)}`}
          >
            {tk.display_status || "-"}
          </span>
          {tk.display_status_reason && (
            <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-max max-w-[200px] bg-slate-800 border border-white/10 text-xs text-slate-300 rounded-lg px-3 py-2 opacity-0 group-hover:opacity-100 transition pointer-events-none text-center">
              {tk.display_status_reason}
              {/* Arrow */}
              <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800" />
            </div>
          )}
        </div>
      </td>

      <td className="px-3 py-4 text-sm text-slate-300">
        {`${formatCurrency(tk.price, tk.currency)} (${tk.commission_amount})`}
      </td>

      <td className="px-3 py-4 text-sm text-slate-300">
        {formatCurrency(tk.total_income, tk.currency)}
      </td>

      <td className="px-3 py-4 text-sm text-slate-300">
        {formatCurrency(tk.total_commission, tk.currency)}
      </td>

      <td className="px-3 py-4 text-sm text-slate-300">
        <div className="flex items-center justify-center gap-1">
          <span
            className={`px-2.5 py-0.5 rounded-md text-xs font-medium ${statusClass(tk.admin_status)}`}
          >
            {tk.admin_status || "-"}
          </span>
          <button
            onClick={() => onStatusClick(tk)}
            className="p-1 rounded text-slate-500 hover:text-white hover:bg-slate-700 transition"
            title="Change status"
          >
            <ChevronDownIcon className="w-3 h-3" />
          </button>
        </div>
      </td>

      <td className="px-3 py-4 text-right">
        <div className="flex justify-end gap-2">
          <button
            onClick={onEdit}
            className="p-2 rounded-md text-slate-300 hover:text-white hover:bg-slate-700 transition"
          >
            <PencilIcon className="w-4 h-4" />
          </button>

          <button
            onClick={onDelete}
            className="p-2 rounded-md text-slate-300 hover:text-red-400 hover:bg-red-500/10 transition"
            title="Delete user"
          >
            <TrashIcon className="w-4 h-4" />
          </button>
        </div>
      </td>
    </tr>
  );
}
